import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, formatApiError, API } from "../lib/api";

const emptyArticle = { title: "", category: "Practical AI Tools", excerpt: "", body: "", image_url: "", read_time: "4 min read" };
const emptyWorkshop = { title: "", date: "", time: "", location: "Live on Zoom (free)", topic: "AI Basics", description: "", seats: 40 };
const categories = ["Practical AI Tools", "Financial Impact", "Scam Prevention", "Social & Ethics"];
const tabs = ["Overview", "Articles", "Workshops", "Signups", "Registrations", "Messages"];

export default function AdminDashboard({ onLogout, onUnauthorized }) {
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [rows, setRows] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingWorkshop, setEditingWorkshop] = useState(null);

  const guard = useCallback((err) => {
    if (err?.response?.status === 401) { onUnauthorized(); return true; }
    toast.error(formatApiError(err));
    return false;
  }, [onUnauthorized]);

  const loadAll = useCallback(async () => {
    try {
      const [s, a, w] = await Promise.all([
        api.get("/admin/stats"), api.get("/articles"), api.get("/workshops"),
      ]);
      setStats(s.data); setArticles(a.data); setWorkshops(w.data);
    } catch (err) { guard(err); }
  }, [guard]);

  const loadRows = useCallback(async (kind) => {
    try {
      const res = await api.get(`/admin/${kind}`);
      setRows(res.data);
    } catch (err) { guard(err); }
  }, [guard]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const map = { Signups: "subscribers", Registrations: "registrations", Messages: "messages" };
    if (map[tab]) loadRows(map[tab]);
  }, [tab, loadRows]);

  const saveArticle = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle.id) await api.put(`/admin/articles/${editingArticle.id}`, editingArticle);
      else await api.post("/admin/articles", editingArticle);
      toast.success("Article saved.");
      setEditingArticle(null); loadAll();
    } catch (err) { guard(err); }
  };

  const saveWorkshop = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editingWorkshop, seats: Number(editingWorkshop.seats) || 0 };
      if (editingWorkshop.id) await api.put(`/admin/workshops/${editingWorkshop.id}`, payload);
      else await api.post("/admin/workshops", payload);
      toast.success("Workshop saved.");
      setEditingWorkshop(null); loadAll();
    } catch (err) { guard(err); }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted.");
      loadAll();
    } catch (err) { guard(err); }
  };

  const exportCsv = async (kind) => {
    try {
      const token = localStorage.getItem("aiwc_token");
      const res = await fetch(`${API}/admin/export/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const input = "w-full rounded-lg border border-stone-700 bg-stone-900 px-4 py-2.5 text-sm text-cream placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-ember/50 focus:border-ember";
  const label = "block text-[11px] font-mono uppercase tracking-[0.18em] text-stone-500 mb-1.5";

  return (
    <div className="min-h-screen bg-ink text-stone-200" data-testid="admin-dashboard">
      <header className="border-b border-stone-800 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-18 py-4 flex items-center justify-between">
          <Logo dark />
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-stone-500 hover:text-cream transition-colors" data-testid="admin-view-site-link">View site</a>
            <button onClick={onLogout} data-testid="admin-logout-button"
                    className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-cream transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
                    className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-colors duration-300 border ${
                      tab === t ? "bg-ember border-ember text-cream" : "border-stone-700 text-stone-400 hover:text-cream hover:border-stone-500"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats-grid">
            {[["Articles", stats.articles], ["Workshops", stats.workshops], ["Signups", stats.subscribers],
              ["Registrations", stats.registrations], ["Messages", stats.messages]].map(([k, v]) => (
              <div key={k} className="bg-stone-900/60 border border-stone-800 rounded-2xl p-6" data-testid={`admin-stat-${k.toLowerCase()}`}>
                <p className="font-serif text-4xl font-bold text-cream">{v}</p>
                <p className="mt-2 text-xs font-mono uppercase tracking-[0.18em] text-stone-500">{k}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "Articles" && (
          <div data-testid="admin-articles-panel">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-cream">Articles</h2>
              <button onClick={() => setEditingArticle({ ...emptyArticle })} data-testid="admin-article-create-button"
                      className="inline-flex items-center gap-2 rounded-full bg-ember text-cream text-xs font-semibold px-5 py-3 hover:bg-ember-dark transition-colors">
                <Plus size={14} /> New article
              </button>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 bg-stone-900/60 border border-stone-800 rounded-xl px-5 py-4" data-testid={`admin-article-row-${a.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-cream truncate">{a.title}</p>
                    <p className="text-xs text-stone-500 mt-1">{a.category}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditingArticle({ ...a })} data-testid={`admin-article-edit-${a.id}`}
                            className="p-2 text-stone-400 hover:text-cream transition-colors"><Pencil size={15} /></button>
                    <button onClick={() => remove("articles", a.id)} data-testid={`admin-article-delete-${a.id}`}
                            className="p-2 text-stone-400 hover:text-ember transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Workshops" && (
          <div data-testid="admin-workshops-panel">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-cream">Workshops</h2>
              <button onClick={() => setEditingWorkshop({ ...emptyWorkshop })} data-testid="admin-workshop-create-button"
                      className="inline-flex items-center gap-2 rounded-full bg-ember text-cream text-xs font-semibold px-5 py-3 hover:bg-ember-dark transition-colors">
                <Plus size={14} /> New workshop
              </button>
            </div>
            <div className="space-y-3">
              {workshops.map((w) => (
                <div key={w.id} className="flex items-center justify-between gap-4 bg-stone-900/60 border border-stone-800 rounded-xl px-5 py-4" data-testid={`admin-workshop-row-${w.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-cream truncate">{w.title}</p>
                    <p className="text-xs text-stone-500 mt-1">{w.date} · {w.time} · {w.registered || 0} registered</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditingWorkshop({ ...w })} data-testid={`admin-workshop-edit-${w.id}`}
                            className="p-2 text-stone-400 hover:text-cream transition-colors"><Pencil size={15} /></button>
                    <button onClick={() => remove("workshops", w.id)} data-testid={`admin-workshop-delete-${w.id}`}
                            className="p-2 text-stone-400 hover:text-ember transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {["Signups", "Registrations", "Messages"].includes(tab) && (
          <div data-testid={`admin-${tab.toLowerCase()}-panel`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-cream">{tab} <span className="text-stone-500 text-lg">({rows.length})</span></h2>
              {tab !== "Messages" && (
                <button onClick={() => exportCsv(tab === "Signups" ? "subscribers" : "registrations")} data-testid="admin-export-csv-button"
                        className="inline-flex items-center gap-2 rounded-full border border-stone-700 text-xs font-semibold px-5 py-3 text-stone-300 hover:border-ember hover:text-cream transition-colors">
                  <Download size={14} /> Export CSV
                </button>
              )}
            </div>
            <div className="space-y-3">
              {rows.length === 0 && <p className="text-sm text-stone-500">Nothing here yet.</p>}
              {rows.map((r) => (
                <div key={r.id} className="bg-stone-900/60 border border-stone-800 rounded-xl px-5 py-4" data-testid={`admin-row-${r.id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-cream">{r.name || "—"} <span className="font-normal text-stone-400">{r.email}</span></p>
                    <p className="text-[11px] text-stone-500">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  {r.workshop_title && <p className="text-xs text-ember mt-1">{r.workshop_title}</p>}
                  {r.message && <p className="text-sm text-stone-300 mt-2 leading-relaxed">{r.message}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editingArticle && (
        <div className="fixed inset-0 z-[70] bg-ink/80 backdrop-blur-sm overflow-y-auto p-4 flex items-start justify-center" data-testid="admin-article-modal">
          <form onSubmit={saveArticle} className="w-full max-w-2xl my-10 bg-stone-950 border border-stone-800 rounded-3xl p-8 space-y-4" data-testid="admin-article-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl text-cream">{editingArticle.id ? "Edit article" : "New article"}</h3>
              <button type="button" onClick={() => setEditingArticle(null)} className="p-2 text-stone-400 hover:text-cream" data-testid="admin-article-cancel"><X size={18} /></button>
            </div>
            <div><label className={label}>Title</label>
              <input required value={editingArticle.title} onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })} className={input} data-testid="admin-article-title-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={label}>Category</label>
                <select value={editingArticle.category} onChange={(e) => setEditingArticle({ ...editingArticle, category: e.target.value })} className={input} data-testid="admin-article-category-select">
                  {categories.map((c) => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={label}>Read time</label>
                <input value={editingArticle.read_time} onChange={(e) => setEditingArticle({ ...editingArticle, read_time: e.target.value })} className={input} /></div>
            </div>
            <div><label className={label}>Excerpt (shown on cards)</label>
              <textarea required rows={2} value={editingArticle.excerpt} onChange={(e) => setEditingArticle({ ...editingArticle, excerpt: e.target.value })} className={`${input} resize-none`} /></div>
            <div><label className={label}>Body (blank line between paragraphs)</label>
              <textarea required rows={10} value={editingArticle.body} onChange={(e) => setEditingArticle({ ...editingArticle, body: e.target.value })} className={`${input} resize-y`} data-testid="admin-article-body-input" /></div>
            <div><label className={label}>Image URL (optional)</label>
              <input value={editingArticle.image_url} onChange={(e) => setEditingArticle({ ...editingArticle, image_url: e.target.value })} className={input} /></div>
            <button type="submit" className="w-full rounded-full bg-ember text-cream text-sm font-semibold py-3.5 hover:bg-ember-dark transition-colors" data-testid="admin-article-save-button">Save article</button>
          </form>
        </div>
      )}

      {editingWorkshop && (
        <div className="fixed inset-0 z-[70] bg-ink/80 backdrop-blur-sm overflow-y-auto p-4 flex items-start justify-center" data-testid="admin-workshop-modal">
          <form onSubmit={saveWorkshop} className="w-full max-w-2xl my-10 bg-stone-950 border border-stone-800 rounded-3xl p-8 space-y-4" data-testid="admin-workshop-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl text-cream">{editingWorkshop.id ? "Edit workshop" : "New workshop"}</h3>
              <button type="button" onClick={() => setEditingWorkshop(null)} className="p-2 text-stone-400 hover:text-cream" data-testid="admin-workshop-cancel"><X size={18} /></button>
            </div>
            <div><label className={label}>Title</label>
              <input required value={editingWorkshop.title} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, title: e.target.value })} className={input} data-testid="admin-workshop-title-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className={label}>Date</label>
                <input required placeholder="Sat, Aug 8, 2026" value={editingWorkshop.date} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, date: e.target.value })} className={input} /></div>
              <div><label className={label}>Time</label>
                <input required placeholder="10:00 AM ET" value={editingWorkshop.time} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, time: e.target.value })} className={input} /></div>
              <div><label className={label}>Seats</label>
                <input type="number" min="1" value={editingWorkshop.seats} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, seats: e.target.value })} className={input} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={label}>Location</label>
                <input required value={editingWorkshop.location} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, location: e.target.value })} className={input} /></div>
              <div><label className={label}>Topic</label>
                <input required value={editingWorkshop.topic} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, topic: e.target.value })} className={input} /></div>
            </div>
            <div><label className={label}>Description</label>
              <textarea required rows={4} value={editingWorkshop.description} onChange={(e) => setEditingWorkshop({ ...editingWorkshop, description: e.target.value })} className={`${input} resize-none`} /></div>
            <button type="submit" className="w-full rounded-full bg-ember text-cream text-sm font-semibold py-3.5 hover:bg-ember-dark transition-colors" data-testid="admin-workshop-save-button">Save workshop</button>
          </form>
        </div>
      )}
    </div>
  );
}
